import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { captureException } from '@sentry/angular';
import { ActivitiesAssignment, Activity, ZOOM_LEVEL } from '@wilson/interfaces';
import { ModalInteractionService } from '@wilson/shift-timeline/core';
import { FindActivityStaffPopupComponent } from '@wilson/shift-timeline/modal';
import {
  ResetRowHeight,
  ResetShiftAssignmentModalState,
  ResetShiftTimelineWidth,
  ResetTimelineInteractionsState,
  SetActivitiesToAssign,
  SetZoomLevel,
  ShiftAssignmentModalState,
  TimelineSetSearchTerm,
  ToggleRadioButtonInTimelineModal,
  ToggleRenderTimelineInModal,
} from '@wilson/shift-timeline/state';
import { ShiftsService } from '@wilson/shifts';
import { NzMessageService } from 'ng-zorro-antd/message';
import {
  ModalButtonOptions,
  NzModalRef,
  NzModalService,
} from 'ng-zorro-antd/modal';
import { firstValueFrom, take } from 'rxjs';

export interface ReloadSignal {
  shouldReload: boolean;
  shouldGoBack: boolean;
  shiftId: string | null;
}

@Injectable()
export class AssignActivityPopupManagerService {
  private translate = inject(TranslateService);
  private nzModalService = inject(NzModalService);
  private store = inject(Store);
  private messageService = inject(NzMessageService);
  private shiftsService = inject(ShiftsService);
  modalInteractionService = inject(ModalInteractionService);

  private isLoading = false;

  public openPopup({
    activitiesToAssign,
    isReadOnlyMode = false,
  }: {
    activitiesToAssign: Activity[];
    isReadOnlyMode?: boolean;
  }): NzModalRef {
    const nzFooterOptions: ModalButtonOptions<FindActivityStaffPopupComponent>[] =
      [
        {
          label: this.translate.instant('general.cancel'),
          type: 'default',
          disabled: () => this.isLoading,
          onClick: (): void => {
            const signal: ReloadSignal = {
              shiftId: null,
              shouldGoBack: false,
              shouldReload: false,
            };
            modal.destroy(signal);
          },
        },
      ];
    if (isReadOnlyMode) {
      this.store.dispatch([new ToggleRadioButtonInTimelineModal()]);
    } else {
      nzFooterOptions.push({
        label: this.translate.instant('page.services.assign'),
        type: 'primary',
        disabled: () => this.modalInteractionService.selectedRowIndex === null,
        onClick: async () => {
          this.isLoading = true;
          const userId = this.modalInteractionService.selectedUserId;
          const shiftAssignment = this.store.selectSnapshot(
            ShiftAssignmentModalState.userShiftAssignment(userId),
          ) as ActivitiesAssignment;
          let shiftId: string | null;
          try {
            if (shiftAssignment?.isExistingShift) {
              shiftId =
                (
                  await firstValueFrom(
                    this.shiftsService.updateShift({
                      ...shiftAssignment.shift,
                      userId: userId,
                    }),
                  )
                ).id ?? null;
            } else {
              shiftId =
                (
                  await firstValueFrom(
                    this.shiftsService.createShifts([
                      {
                        ...shiftAssignment.shift,
                        userId: userId,
                      },
                    ]),
                  )
                )[0].id ?? null;
            }
            const signal: ReloadSignal = {
              shiftId: shiftId,
              shouldGoBack: false,
              shouldReload: true,
            };
            modal.destroy(signal);
            this.isLoading = false;
            this.messageService.success(
              this.translate.instant('general.saved_successfully'),
            );
          } catch (error) {
            captureException(error);
            this.messageService.error(
              this.translate.instant('assign.activity.popup.saving.error'),
            );
          }
        },
      });
    }
    this.store.dispatch([
      new ToggleRenderTimelineInModal(),
      new SetZoomLevel(ZOOM_LEVEL.DAY),
      new SetActivitiesToAssign(activitiesToAssign),
    ]);

    const modal: NzModalRef = this.nzModalService.create({
      nzTitle: this.translate.instant('shift.assignment.check-alternative'),
      nzContent: FindActivityStaffPopupComponent,
      nzWidth: '98%',
      nzClosable: false,
      nzFooter: nzFooterOptions,
    });
    modal.afterClose.pipe(take(1)).subscribe(() => this.resetModalStates());
    return modal;
  }

  private resetModalStates(): void {
    this.store.dispatch([
      new ToggleRenderTimelineInModal(),
      new ResetTimelineInteractionsState(),
      new ResetShiftAssignmentModalState(),
      new ResetShiftTimelineWidth(),
      new SetActivitiesToAssign([]),
      new TimelineSetSearchTerm(''),
      new ResetRowHeight(),
    ]);
    this.modalInteractionService.resetModalState();
  }
}
